/**
 * The `.{ }` sigil grammar, mirrored from the backend.
 *
 * The authority is `com.a2a.scheduler.domain.cohort.SigilSyntax` and `SigilType`. This file
 * exists because the composer has to recognise a half-typed sigil before anything is sent, and
 * it must stay in step with that class: `.{` is provisional, so it is spelled out in ONE
 * constant here exactly as it is there, and changing the prefix stays a one-line change on
 * each side rather than a search.
 *
 * The companion for individuals is `mentions.ts`. A person is `@jane`, not a sigil, because
 * that is the notation the composer already teaches.
 */

/** Opens a sigil. Provisional; mirrors SigilSyntax.OPEN. */
export const SIGIL_OPEN = ".{";

/** Closes a sigil. Mirrors SigilSyntax.CLOSE. */
export const SIGIL_CLOSE = "}";

/** Longest name accepted between the braces. Mirrors SigilSyntax.MAX_NAME_LENGTH. */
export const SIGIL_MAX_NAME_LENGTH = 64;

/** What a sigil names. Every kind carries a type character; there is no bare form. */
export type SigilKind = "pattern" | "department" | "role";

export interface SigilTypeSpec {
  kind: SigilKind;
  /** The single character that introduces this type inside the braces. Mandatory. */
  marker: string;
  /** What the chooser calls it. */
  label: string;
  /** False for the org types: they parse, and there is no directory to resolve them against. */
  resolvable: boolean;
}

/**
 * The three types, in the order the chooser lists them, mirroring `SigilType`.
 *
 * The type character is mandatory since 2026-09-12: a bare `.{name}` stopped being a sigil when
 * the grammar gained several types, because an autocomplete cannot tell a half-typed name from a
 * half-typed prefix. `#` is not contact groups returning (they went with #98); it was reassigned
 * to the availability pattern, which is the box of contacts a group used to be.
 *
 * Departments and roles are listed even though nothing can resolve them yet. That is the point
 * of showing the types first: the chooser is what teaches the grammar, and a type that is coming
 * is more useful to see greyed out than to not see at all.
 */
export const SIGIL_TYPES: SigilTypeSpec[] = [
  { kind: "pattern", marker: "#", label: "Availability pattern", resolvable: true },
  { kind: "department", marker: "^", label: "Department", resolvable: false },
  { kind: "role", marker: "!", label: "Role", resolvable: false },
];

/** The type introduced by `marker`, or undefined. Every type has a marker now. */
export function sigilTypeForMarker(marker: string): SigilTypeSpec | undefined {
  return SIGIL_TYPES.find((type) => type.marker === marker);
}

/** Render a sigil the way a user would type it, e.g. `.{#Sales}`. */
export function renderSigil(kind: SigilKind, name: string): string {
  const type = SIGIL_TYPES.find((t) => t.kind === kind)!;
  return `${SIGIL_OPEN}${type.marker}${name}${SIGIL_CLOSE}`;
}

/**
 * The half-typed sigil the caret is currently inside.
 *
 * `kind` is null until a type character settles it: that is the state in which the chooser
 * shows the three types. Typing the character directly (`.{#`) settles the kind and the menu
 * skips straight to the filtered list, because typing the character IS picking the type and
 * charging an expert a step for it every time would be a tax on knowing the grammar.
 *
 * An unmarked body (`.{Fam`) also has a null `kind`: it is NOT read as a name of anything,
 * because there is no bare form any more. The chooser stays open prompting for a type, and the
 * body is kept as `query` so picking a type does not throw away what was typed.
 */
export interface ActiveSigil {
  /** Index of the `.` in the value. */
  start: number;
  /** The type, once a character has settled it; null while the chooser should show. */
  kind: SigilKind | null;
  /** Text typed after the marker up to the caret (may be empty). */
  query: string;
}

/**
 * If the caret sits inside an unclosed `.{…`, describe it; otherwise null.
 *
 * Scans back from the caret for the opening `.{`, and gives up at a `}` (the sigil is already
 * closed and the caret is past it), at a newline (a sigil never spans lines), or once the run
 * is longer than a name can be. There is no whitespace rule on `.{`, unlike `@`: nothing in
 * ordinary text puts a `{` straight after a `.`, and requiring whitespace would silently drop
 * the parenthesised `(.{#sales})`.
 */
export function activeSigil(value: string, caret: number): ActiveSigil | null {
  const limit = Math.max(0, caret - SIGIL_MAX_NAME_LENGTH - SIGIL_OPEN.length - 1);
  for (let i = caret - 1; i >= limit; i--) {
    const ch = value[i];
    if (ch === SIGIL_CLOSE || ch === "\n" || ch === "\r" || ch === "{") {
      // "{" alone (rather than the full ".{") means a nested or stray brace: not a sigil.
      if (ch === "{" && value.slice(i - 1, i + 1) === SIGIL_OPEN) {
        return read(value, i - 1, caret);
      }
      return null;
    }
  }
  return null;
}

function read(value: string, start: number, caret: number): ActiveSigil {
  const body = value.slice(start + SIGIL_OPEN.length, caret);
  const type = sigilTypeForMarker(body.charAt(0));
  return {
    start,
    kind: type ? type.kind : null,
    query: type ? body.slice(1) : body,
  };
}

// addressesACohort lived here until step 5: the browser no longer decides which pipeline reads a
// message (design 5.1). The identical closed-sigil test now runs server-side in TurnRouter, as
// the transitional routing rule that dies at step 5b.
